import { WithStatePaginatedTable } from '@delmonicos/shared-design-system-react/src/components/with-state-paginated-table/with-state-paginated-table';
import { TransformedPaginatedData } from '@delmonicos/shared-sdk/libs/cpms/src/clients/utils/transform-api-paginated-response/transformed-paginated-data.model';
import { TariffModel } from '@delmonicos/shared-sdk/libs/cpms/src/libs/ocpi/src/ocpi-cpo-api/models/tariffs/tariff.model';
import {
  ColumnOrderState,
  OnChangeFn,
  SortingState,
  VisibilityState,
  createColumnHelper,
} from '@tanstack/react-table';
import { useMemo } from 'react';
import { useTranslation } from 'react-i18next';

import { StateEnum } from '@model/enum/state.enum';

import { EmspBoTariffListCurrencyCell } from './emsp-bo-tariff-list-currency.cell';
import { EmspBoTariffListIdCell } from './emsp-bo-tariff-list-id.cell';
import { EmspBoTariffListLastUpdatedCell } from './emsp-bo-tariff-list-last-updated.cell';
import {
  EMSP_BO_TARIFF_LIST_COLUMN_ORDER,
  EMSP_BO_TARIFF_LIST_COLUMN_VISIBILITY,
} from './emsp-bo-tariff-list.config';

const columnHelper = createColumnHelper<TariffModel>();

interface EmspBoTariffListViewProps {
  paginationLimit: number;
  paginationLimitList: number[];
  paginationState: StateEnum;
  paginationSorting: SortingState;
  resource?: TransformedPaginatedData<TariffModel>;
  columnVisibility?: VisibilityState;
  columnOrder?: ColumnOrderState;
  onPaginationLimitChange: (paginationLimitNext: number) => void;
  onPaginationPageChange: (paginationPageNext: number) => void;
  onPaginationSortingChange?: OnChangeFn<SortingState>;
  renderColumnSettings?: (close: () => void) => React.ReactNode;
  onHideColumn?: (columnId: string) => void;
  onColumnOrderChange?: OnChangeFn<ColumnOrderState>;
}

export const EmspBoTariffListView = ({
  paginationLimit,
  paginationLimitList,
  paginationState,
  paginationSorting,
  resource,
  columnVisibility = EMSP_BO_TARIFF_LIST_COLUMN_VISIBILITY,
  columnOrder = EMSP_BO_TARIFF_LIST_COLUMN_ORDER,
  onPaginationLimitChange,
  onPaginationPageChange,
  onPaginationSortingChange,
  renderColumnSettings,
  onHideColumn,
  onColumnOrderChange,
}: EmspBoTariffListViewProps): React.JSX.Element => {
  // CONTEXT ///////////////////////////////////////////////////////////////////

  const { t } = useTranslation();

  // USER PERMISSIONS //////////////////////////////////////////////////////////

  // Nothing to do

  // LIST //////////////////////////////////////////////////////////////////////

  const columns = useMemo(
    () => [
      columnHelper.accessor('id', {
        enableSorting: false,
        header: t('delmonicos-shared-sdk.ocpi-cpo-api-models-tariff.id'),
        cell: EmspBoTariffListIdCell,
      }),
      columnHelper.accessor('currency', {
        enableSorting: false,
        header: t('delmonicos-shared-sdk.ocpi-cpo-api-models-tariff.currency'),
        cell: EmspBoTariffListCurrencyCell,
      }),
      columnHelper.accessor('last_updated', {
        enableSorting: false,
        header: t(
          'delmonicos-shared-sdk.ocpi-cpo-api-models-tariff.last-updated',
        ),
        cell: EmspBoTariffListLastUpdatedCell,
      }),
    ],
    [t],
  );

  // RENDER ////////////////////////////////////////////////////////////////////

  return (
    <WithStatePaginatedTable
      paginatedData={resource}
      state={paginationState}
      sorting={paginationSorting}
      columns={columns}
      columnOrder={columnOrder}
      columnVisibility={columnVisibility}
      limit={paginationLimit}
      limitList={paginationLimitList}
      errorMessage={t('$.emsp-bo-tariff-list.error')}
      onLimitChange={onPaginationLimitChange}
      onPageChange={onPaginationPageChange}
      onSortingChange={onPaginationSortingChange}
      renderColumnSettings={renderColumnSettings}
      onHideColumn={onHideColumn}
      onColumnOrderChange={onColumnOrderChange}
    />
  );
};
